import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

const STAGE_OPTIONS = ['Planning', 'Design', 'Consent', 'Construction', 'Handover'];
const CONSTRUCTION_SUB_STAGE_OPTIONS = ['Base', 'Frame', 'Lock Up', 'Fixing', 'Completion', 'Settlement'];

export type GanttScale = 'day' | 'week' | 'month' | 'year';

interface Props {
  itemCount: number;
  canEdit: boolean;
  viewMode: 'list' | 'gantt';
  onToggleView: () => void;
  onRefresh: () => void;
  onGenerateTimeline: () => void;
  onImportBuildertrend?: () => void;
  completionFilter: 'unfinished' | 'completed' | 'all';
  onSetCompletionFilter: (f: 'unfinished' | 'completed' | 'all') => void;
  mainStageFilter: string;
  onSetMainStageFilter: (v: string) => void;
  constructionSubStageFilter: string;
  onSetConstructionSubStageFilter: (v: string) => void;
  // NEW: pending schedule filter props
  pendingFilter?: 'all' | 'pending_final' | 'final_100' | 'no_mapping' | 'incomplete_checklist';
  onSetPendingFilter?: (f: 'all' | 'pending_final' | 'final_100' | 'no_mapping' | 'incomplete_checklist') => void;
  // NEW bulk selection
  selectedItemIds?: Set<string>;
  onToggleSelectItem?: (itemId: string) => void;
  onSelectAllVisible?: () => void;
  onClearSelection?: () => void;
  onBulkMarkComplete?: () => void;
  onMarkAllPendingComplete?: () => void;
  pendingCount?: number;
  bulkMarking?: boolean;
  canBulkSelect?: boolean;
  onToggleFullscreen: () => void;
  isFullscreen: boolean;
  onIncludeAll: () => void;
  onExcludeAll: () => void;
  showExcluded: boolean;
  onToggleShowExcluded: () => void;
  onRecalcCriticalPath?: () => void;
  onRecalcLinks?: () => void;
  onRecalculateProgress?: () => void;
  onSetBaseline?: () => void;
  onToggleDebug?: () => void;
  onAddItem?: () => void;
  saving?: boolean;
  showAllLinks?: boolean;
  onToggleShowAllLinks?: () => void;
  isMobile?: boolean;
  showSummary?: boolean;
  onToggleSummary?: () => void;
  // ── New utility features ──
  ganttScale?: GanttScale;
  onSetGanttScale?: (v: GanttScale) => void;
  showPhases?: boolean;
  onToggleShowPhases?: () => void;
  criticalPathEnabled?: boolean;
  showCriticalPathOnly?: boolean;
  onToggleCriticalPathOnly?: () => void;
  onOpenFilterPanel?: () => void;
  onOpenHistoryPanel?: () => void;
  onOpenExportPanel?: () => void;
  hasActiveFilters?: boolean;
  onScrollToToday?: () => void;
  criticalItemCount?: number;
  totalItemCount?: number;
  onResetCriticalPathFlags?: () => void;
  // NEW: copy Gantt colors from 5 Methuen Road
  onCopyGanttColorsFromMethuen?: () => void;
}

// Scale options labels are set inside the component using t()

export default function ScheduleToolbar({
  itemCount,
  canEdit,
  viewMode,
  onToggleView,
  onRefresh,
  onGenerateTimeline,
  onImportBuildertrend,
  completionFilter,
  onSetCompletionFilter,
  mainStageFilter,
  onSetMainStageFilter,
  constructionSubStageFilter,
  onSetConstructionSubStageFilter,
  onToggleFullscreen,
  isFullscreen,
  onIncludeAll,
  onExcludeAll,
  showExcluded,
  onToggleShowExcluded,
  onRecalcCriticalPath,
  onRecalcLinks,
  onRecalculateProgress,
  onSetBaseline,
  onToggleDebug,
  onAddItem,
  saving = false,
  showAllLinks = false,
  onToggleShowAllLinks,
  isMobile = false,
  showSummary = true,
  onToggleSummary,
  // New features
  ganttScale = 'week',
  onSetGanttScale,
  showPhases = false,
  onToggleShowPhases,
  criticalPathEnabled = false,
  showCriticalPathOnly = false,
  onToggleCriticalPathOnly,
  onOpenFilterPanel,
  onOpenHistoryPanel,
  onOpenExportPanel,
  hasActiveFilters = false,
  onScrollToToday,
  criticalItemCount = 0,
  totalItemCount = 0,
  onResetCriticalPathFlags,
  // NEW: copy Gantt colors from 5 Methuen Road
  onCopyGanttColorsFromMethuen,
  // NEW pending filter
  pendingFilter = 'all',
  onSetPendingFilter,
  // NEW bulk selection
  selectedItemIds,
  onToggleSelectItem,
  onSelectAllVisible,
  onClearSelection,
  onBulkMarkComplete,
  onMarkAllPendingComplete,
  pendingCount = 0,
  bulkMarking = false,
  canBulkSelect = false,
}: Props) {
  const { t } = useTranslation();
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [showMobileMenu, setShowMobileMenu] = useState(false);

  // Fix 7: Zoom level labels — Compact/Comfortable/Wide are more user-friendly
  const SCALE_OPTIONS: { value: GanttScale; label: string; icon: string }[] = [
    { value: 'day', label: t('toolbar.wideZoom'), icon: 'ri-zoom-in-line' },
    { value: 'week', label: t('toolbar.comfortableZoom'), icon: 'ri-calendar-event-line' },
    { value: 'month', label: t('toolbar.compactZoom'), icon: 'ri-zoom-out-line' },
    { value: 'year', label: t('toolbar.miniZoom'), icon: 'ri-calendar-line' },
  ];
  const advRef = useRef<HTMLDivElement>(null);
  const mobileRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function onDocClick(e: MouseEvent) {
      if (advRef.current && !advRef.current.contains(e.target as Node)) {
        setShowAdvanced(false);
      }
      if (mobileRef.current && !mobileRef.current.contains(e.target as Node)) {
        setShowMobileMenu(false);
      }
    }
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  if (isMobile) {
    return (
      <div className="space-y-2">
        {/* Primary row */}
        <div className="flex items-center gap-2 overflow-x-auto scrollbar-hide">
          {/* Mobile view mode: List | Gantt */}
          <div className="flex bg-gray-100 rounded-lg p-0.5 flex-shrink-0">
            <button
              onClick={() => { if (viewMode !== 'list') onToggleView(); }}
              className={`px-2.5 py-1.5 rounded-md text-[11px] font-medium cursor-pointer whitespace-nowrap transition-colors ${viewMode === 'list' ? 'bg-white text-[#1A1A1A] shadow-sm' : 'text-[#6B7280]'}`}
            >
              <i className="ri-list-check mr-0.5"></i>{t('toolbar.list')}
            </button>
            <button
              onClick={() => { if (viewMode !== 'gantt') onToggleView(); }}
              className={`px-2.5 py-1.5 rounded-md text-[11px] font-medium cursor-pointer whitespace-nowrap transition-colors ${viewMode === 'gantt' ? 'bg-white text-[#1A1A1A] shadow-sm' : 'text-[#6B7280]'}`}
            >
              <i className="ri-bar-chart-grouped-fill mr-0.5"></i>{t('toolbar.gantt')}
            </button>
          </div>

          {/* Scale selector */}
          {viewMode === 'gantt' && onSetGanttScale && (
            <div className="flex bg-gray-100 rounded-lg p-0.5 flex-shrink-0">
              {SCALE_OPTIONS.map((s) => (
                <button
                  key={s.value}
                  onClick={() => onSetGanttScale(s.value)}
                  className={`px-2 py-1.5 rounded-md text-[10px] font-medium cursor-pointer whitespace-nowrap transition-colors ${
                    ganttScale === s.value ? 'bg-white text-[#1A1A1A] shadow-sm' : 'text-[#6B7280]'
                  }`}
                  title={s.label}
                >
                  <i className={`${s.icon} mr-0.5`}></i>
                  {s.label}
                </button>
              ))}
            </div>
          )}

          {/* Today */}
          {viewMode === 'gantt' && onScrollToToday && (
            <button
              onClick={onScrollToToday}
              className="flex-shrink-0 flex items-center gap-1 bg-white border border-gray-200 text-[#374151] px-2.5 py-1.5 rounded-lg text-[11px] font-semibold cursor-pointer whitespace-nowrap hover:bg-gray-50 transition-colors"
            >
              <i className="ri-calendar-check-line text-[#E8621A]"></i>{t('toolbar.today')}
            </button>
          )}

          {/* Regenerate */}
          {canEdit && (
            <button
              onClick={onGenerateTimeline}
              disabled={saving}
              className="flex-shrink-0 flex items-center gap-1 bg-[#E8621A] text-white px-2.5 py-1.5 rounded-lg text-[11px] font-semibold cursor-pointer whitespace-nowrap disabled:opacity-50 hover:bg-[#B84C10] transition-colors"
            >
              <i className="ri-calendar-event-line"></i>
              {t('toolbar.regenerate')}
            </button>
          )}

          {canEdit && onImportBuildertrend && (
            <button
              onClick={onImportBuildertrend}
              disabled={saving}
              className="flex-shrink-0 flex items-center gap-1 bg-white border border-gray-200 text-[#374151] px-2.5 py-1.5 rounded-lg text-[11px] font-semibold cursor-pointer whitespace-nowrap disabled:opacity-50 hover:bg-gray-50 transition-colors"
            >
              <i className="ri-file-upload-line text-[#E8621A]"></i>
              {t('toolbar.importPdf')}
            </button>
          )}

          {/* Filter */}
          {onOpenFilterPanel && (
            <button
              onClick={onOpenFilterPanel}
              className={`flex-shrink-0 flex items-center gap-1 border px-2.5 py-1.5 rounded-lg text-[11px] font-semibold cursor-pointer whitespace-nowrap transition-colors ${
                hasActiveFilters
                  ? 'bg-[#FEF0E7] border-[#E8621A] text-[#E8621A]'
                  : 'bg-white border-gray-200 text-[#374151] hover:bg-gray-50'
              }`}
            >
              <i className="ri-filter-3-line"></i>
              {t('toolbar.filter')}
            </button>
          )}

          {/* Mobile advanced drawer trigger */}
          {canEdit && (
            <div className="relative flex-shrink-0" ref={mobileRef}>
              <button
                onClick={() => setShowMobileMenu((v) => !v)}
                className="flex items-center gap-1 bg-white border border-gray-200 text-[#6B7280] px-2.5 py-1.5 rounded-lg text-[11px] font-semibold cursor-pointer whitespace-nowrap"
              >
                <i className="ri-more-line"></i>More
              </button>
              {showMobileMenu && (
                <div className="absolute right-0 top-full mt-1 bg-white border border-gray-200 rounded-xl shadow-lg z-50 w-52 py-1">
                  <div className="px-3 py-2 border-b border-gray-100">
                    <p className="text-[10px] font-bold text-[#9CA3AF] uppercase tracking-wider">{t('toolbar.scheduleOnline')}</p>
                  </div>
                  <button onClick={() => { setShowMobileMenu(false); onRefresh(); }} className="w-full text-left px-3 py-2 text-xs text-[#374151] hover:bg-gray-50 cursor-pointer flex items-center gap-2">
                    <i className="ri-refresh-line text-[#9CA3AF]"></i>{t('toolbar.refresh')}
                  </button>
                  {onImportBuildertrend && (
                    <button onClick={() => { setShowMobileMenu(false); onImportBuildertrend(); }} className="w-full text-left px-3 py-2 text-xs text-[#374151] hover:bg-gray-50 cursor-pointer flex items-center gap-2">
                      <i className="ri-file-upload-line text-[#E8621A]"></i>{t('toolbar.importBuildertrend')}
                    </button>
                  )}
                  {onCopyGanttColorsFromMethuen && (
                    <button onClick={() => { setShowMobileMenu(false); onCopyGanttColorsFromMethuen(); }} className="w-full text-left px-3 py-2 text-xs text-[#374151] hover:bg-gray-50 cursor-pointer flex items-center gap-2">
                      <i className="ri-palette-line text-[#EC2590]"></i>{t('toolbar.copyGanttColors')}
                    </button>
                  )}
                  {onAddItem && (
                    <button onClick={() => { setShowMobileMenu(false); onAddItem(); }} className="w-full text-left px-3 py-2 text-xs text-[#374151] hover:bg-gray-50 cursor-pointer flex items-center gap-2">
                      <i className="ri-add-line text-[#9CA3AF]"></i>{t('toolbar.addItem')}
                    </button>
                  )}
                  {viewMode === 'gantt' && onToggleShowAllLinks && (
                    <button onClick={() => { setShowMobileMenu(false); onToggleShowAllLinks(); }} className="w-full text-left px-3 py-2 text-xs text-[#374151] hover:bg-gray-50 cursor-pointer flex items-center gap-2">
                      <i className="ri-links-line text-[#9CA3AF]"></i>{showAllLinks ? t('toolbar.hideLinks') : t('toolbar.showLinks')}
                    </button>
                  )}
                  {viewMode === 'gantt' && onToggleShowPhases && (
                    <button onClick={() => { setShowMobileMenu(false); onToggleShowPhases(); }} className="w-full text-left px-3 py-2 text-xs text-[#374151] hover:bg-gray-50 cursor-pointer flex items-center gap-2">
                      <i className="ri-stack-line text-[#9CA3AF]"></i>{showPhases ? t('toolbar.hidePhases') : t('toolbar.showPhases')}
                    </button>
                  )}
                  {viewMode === 'gantt' && onToggleCriticalPathOnly && (
                    <button onClick={() => { setShowMobileMenu(false); onToggleCriticalPathOnly(); }} className="w-full text-left px-3 py-2 text-xs text-[#374151] hover:bg-gray-50 cursor-pointer flex items-center gap-2">
                      <i className="ri-route-line text-[#EF4444]"></i>{showCriticalPathOnly ? t('toolbar.allItems') : t('toolbar.criticalPathOnly')}
                    </button>
                  )}
                  <button onClick={() => { setShowMobileMenu(false); onToggleShowExcluded(); }} className="w-full text-left px-3 py-2 text-xs text-[#374151] hover:bg-gray-50 cursor-pointer flex items-center gap-2">
                    <i className={`${showExcluded ? 'ri-eye-off-line' : 'ri-eye-line'} text-[#9CA3AF]`}></i>{showExcluded ? t('toolbar.hideExcludedMobile') : t('toolbar.showExcludedMobile')}
                  </button>
                  {onOpenHistoryPanel && (
                    <button onClick={() => { setShowMobileMenu(false); onOpenHistoryPanel(); }} className="w-full text-left px-3 py-2 text-xs text-[#374151] hover:bg-gray-50 cursor-pointer flex items-center gap-2">
                      <i className="ri-history-line text-[#9CA3AF]"></i>{t('toolbar.scheduleHistory')}
                    </button>
                  )}
                  {onOpenExportPanel && (
                    <button onClick={() => { setShowMobileMenu(false); onOpenExportPanel(); }} className="w-full text-left px-3 py-2 text-xs text-[#374151] hover:bg-gray-50 cursor-pointer flex items-center gap-2">
                      <i className="ri-download-line text-[#9CA3AF]"></i>{t('btn.download')}
                    </button>
                  )}
                  <div className="border-t border-gray-100 my-1"></div>
                  {onRecalcCriticalPath && (
                    <button onClick={() => { setShowMobileMenu(false); onRecalcCriticalPath(); }} className="w-full text-left px-3 py-2 text-xs text-[#374151] hover:bg-gray-50 cursor-pointer flex items-center gap-2">
                      <i className="ri-route-line text-[#EF4444]"></i>{t('toolbar.recalcCriticalPath')}
                    </button>
                  )}
                  {onSetBaseline && (
                    <button onClick={() => { setShowMobileMenu(false); onSetBaseline(); }} className="w-full text-left px-3 py-2 text-xs text-[#374151] hover:bg-gray-50 cursor-pointer flex items-center gap-2">
                      <i className="ri-flag-line text-[#9CA3AF]"></i>{t('toolbar.setBaseline')}
                    </button>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Stage filter row */}
        <div className="flex items-center gap-2 flex-wrap">
          <select
            value={mainStageFilter}
            onChange={(e) => { onSetMainStageFilter(e.target.value); onSetConstructionSubStageFilter('all'); }}
            className="bg-white border border-gray-200 rounded-lg px-2.5 py-1 text-[11px] text-[#374151] cursor-pointer"
          >
            <option value="all">{t('toolbar.allStages')}</option>
            {STAGE_OPTIONS.map((s) => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>
          {mainStageFilter === 'Construction' && (
            <select
              value={constructionSubStageFilter}
              onChange={(e) => onSetConstructionSubStageFilter(e.target.value)}
              className="bg-white border border-gray-200 rounded-lg px-2.5 py-1 text-[11px] text-[#374151] cursor-pointer"
            >
              <option value="all">{t('toolbar.allSubStages')}</option>
              {CONSTRUCTION_SUB_STAGE_OPTIONS.map((s) => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
          )}
          {(mainStageFilter !== 'all' || constructionSubStageFilter !== 'all') && (
            <button
              onClick={() => { onSetMainStageFilter('all'); onSetConstructionSubStageFilter('all'); }}
              className="text-[#9CA3AF] text-[11px] cursor-pointer hover:text-[#E8621A] transition-colors whitespace-nowrap"
            >
              <i className="ri-close-circle-line mr-0.5"></i>{t('toolbar.clearFilter')}
            </button>
          )}
        </div>

        {/* NEW: Pending status filter row (mobile) */}
        {onSetPendingFilter && (
          <div className="flex items-center gap-1.5 flex-wrap overflow-x-auto scrollbar-hide">
            <span className="text-[10px] font-bold text-[#9CA3AF] uppercase tracking-wider mr-1 flex-shrink-0">{t('toolbar.statusLabel')}</span>
            {[
              { key: 'all', label: t('toolbar.statusAll') },
              { key: 'pending_final', label: t('toolbar.statusPendingMobile') },
              { key: 'final_100', label: t('toolbar.statusDoneMobile') },
              { key: 'no_mapping', label: t('toolbar.statusNoCLMobile') },
              { key: 'incomplete_checklist', label: t('toolbar.statusIncCLMobile') },
            ].map((opt) => (
              <button
                key={opt.key}
                onClick={() => onSetPendingFilter(opt.key as typeof pendingFilter)}
                className={`px-2 py-0.5 rounded-full text-[10px] font-semibold cursor-pointer whitespace-nowrap transition-colors flex-shrink-0 ${
                  pendingFilter === opt.key
                    ? 'bg-[#E8621A] text-white'
                    : 'bg-white border border-gray-200 text-[#374151] hover:bg-gray-50'
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>
        )}

        {/* NEW: Bulk selection bar (mobile) */}
        {canBulkSelect && selectedItemIds && selectedItemIds.size > 0 && (
          <div className="flex items-center gap-2 bg-[#FEF0E7] border border-[#FDBA74] rounded-xl px-3 py-2">
            <span className="text-[#92400E] text-[11px] font-semibold">
              {selectedItemIds.size} {t('toolbar.selected')}
            </span>
            <div className="flex items-center gap-2 ml-auto">
              <button
                onClick={onSelectAllVisible}
                className="text-[#92400E] text-[10px] font-semibold cursor-pointer whitespace-nowrap hover:underline"
              >
                {t('toolbar.selectAllMobile')}
              </button>
              <button
                onClick={onClearSelection}
                className="text-[#9CA3AF] text-[10px] font-semibold cursor-pointer whitespace-nowrap hover:text-[#374151]"
              >
                {t('toolbar.clear')}
              </button>
              <button
                onClick={onMarkAllPendingComplete}
                disabled={bulkMarking}
                className="flex items-center gap-0.5 px-2 py-1 rounded-lg bg-[#059669] text-white text-[10px] font-bold cursor-pointer whitespace-nowrap disabled:opacity-50 hover:bg-[#047857] transition-colors"
              >
                {bulkMarking ? (
                  <><i className="ri-loader-4-line animate-spin"></i>...</>
                ) : (
                  <><i className="ri-checkbox-multiple-line"></i>{t('toolbar.markAllPendingMobile')}</>
                )}
              </button>
              <button
                onClick={onBulkMarkComplete}
                disabled={bulkMarking}
                className="flex items-center gap-0.5 px-2 py-1 rounded-lg bg-[#E8621A] text-white text-[10px] font-bold cursor-pointer whitespace-nowrap disabled:opacity-50 hover:bg-[#B84C10] transition-colors"
              >
                {bulkMarking ? (
                  <><i className="ri-loader-4-line animate-spin"></i>...</>
                ) : (
                  <><i className="ri-checkbox-circle-line"></i>{t('toolbar.completeMobile')}</>
                )}
              </button>
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {/* Primary row — left and right groups */}
      <div className="flex items-center justify-between gap-2 flex-wrap">
        {/* LEFT group: View, Scale, Today, Refresh */}
        <div className="flex items-center gap-2 flex-wrap">
          {/* Refresh */}
          <button onClick={onRefresh} className="flex items-center gap-1.5 bg-white border border-gray-200 text-[#374151] px-2.5 py-1.5 rounded-lg text-xs font-semibold cursor-pointer whitespace-nowrap hover:bg-gray-50 transition-colors" title={t('toolbar.refresh')}>
            <i className="ri-refresh-line"></i>
          </button>

          {/* View mode */}
          <div className="flex bg-gray-100 rounded-lg p-0.5">
            <button onClick={() => { if (viewMode !== 'list') onToggleView(); }}
              className={`px-3 py-1.5 rounded-md text-xs font-medium cursor-pointer whitespace-nowrap transition-colors ${viewMode === 'list' ? 'bg-white text-[#1A1A1A] shadow-sm' : 'text-[#6B7280]'}`}>
              <i className="ri-list-check mr-1"></i>{t('toolbar.list')}
            </button>
            <button onClick={() => { if (viewMode !== 'gantt') onToggleView(); }}
              className={`px-3 py-1.5 rounded-md text-xs font-medium cursor-pointer whitespace-nowrap transition-colors ${viewMode === 'gantt' ? 'bg-white text-[#1A1A1A] shadow-sm' : 'text-[#6B7280]'}`}>
              <i className="ri-bar-chart-grouped-fill mr-1"></i>{t('toolbar.gantt')}
            </button>
          </div>

          {/* Gantt Scale selector */}
          {viewMode === 'gantt' && onSetGanttScale && (
            <div className="flex bg-gray-100 rounded-lg p-0.5">
              {SCALE_OPTIONS.map((s) => (
                <button
                  key={s.value}
                  onClick={() => onSetGanttScale(s.value)}
                  className={`px-2.5 py-1.5 rounded-md text-[11px] font-medium cursor-pointer whitespace-nowrap transition-colors ${
                    ganttScale === s.value ? 'bg-white text-[#1A1A1A] shadow-sm' : 'text-[#6B7280]'
                  }`}
                  title={s.label}
                >
                  {s.label}
                </button>
              ))}
            </div>
          )}

          {/* Today button */}
          {viewMode === 'gantt' && onScrollToToday && (
            <button
              onClick={onScrollToToday}
              className="flex items-center gap-1.5 bg-white border border-gray-200 text-[#374151] px-3 py-1.5 rounded-lg text-xs font-semibold cursor-pointer whitespace-nowrap hover:bg-gray-50 transition-colors"
              title={t('toolbar.today')}
            >
              <i className="ri-calendar-check-line text-[#E8621A]"></i>
              {t('toolbar.today')}
            </button>
          )}
        </div>

        {/* RIGHT group: Filter, Phases, Critical, Fullscreen, More Actions */}
        <div className="flex items-center gap-2 flex-wrap">
          {/* Filter button */}
          {onOpenFilterPanel && (
            <button
              onClick={onOpenFilterPanel}
              className={`flex items-center gap-1.5 border px-3 py-1.5 rounded-lg text-xs font-semibold cursor-pointer whitespace-nowrap transition-colors ${
                hasActiveFilters
                  ? 'bg-[#FEF0E7] border-[#E8621A] text-[#E8621A]'
                  : 'bg-white border-gray-200 text-[#374151] hover:bg-gray-50'
              }`}
            >
              <i className="ri-filter-3-line"></i>
              {hasActiveFilters ? t('toolbar.filterActive') : t('toolbar.filter')}
            </button>
          )}

          {/* Phases toggle */}
          {viewMode === 'gantt' && onToggleShowPhases && (
            <button
              onClick={onToggleShowPhases}
              className={`flex items-center gap-1.5 border px-3 py-1.5 rounded-lg text-xs font-semibold cursor-pointer whitespace-nowrap transition-colors ${
                showPhases
                  ? 'bg-[#FEF0E7] border-[#E8621A] text-[#E8621A]'
                  : 'bg-white border-gray-200 text-[#374151] hover:bg-gray-50'
              }`}
              title={t('toolbar.phases')}
            >
              <i className="ri-stack-line"></i>
              {t('toolbar.phases')}
            </button>
          )}

          {/* Critical path toggle - requires enabling first */}
          {viewMode === 'gantt' && onToggleCriticalPathOnly && (
            <button
              onClick={() => {
                if (!criticalPathEnabled && onRecalcCriticalPath) {
                  onRecalcCriticalPath();
                  return;
                }
                onToggleCriticalPathOnly();
              }}
              className={`flex items-center gap-1.5 border px-3 py-1.5 rounded-lg text-xs font-semibold cursor-pointer whitespace-nowrap transition-colors ${
                !criticalPathEnabled
                  ? 'bg-gray-100 border-gray-200 text-[#9CA3AF]'
                  : showCriticalPathOnly
                    ? 'bg-[#FEF2F2] border-[#EF4444] text-[#EF4444]'
                    : 'bg-white border-gray-200 text-[#374151] hover:bg-gray-50'
              }`}
              title={!criticalPathEnabled ? t('toolbar.enableCritical') : `${criticalItemCount} critical items of ${totalItemCount}`}
            >
              <i className="ri-route-line"></i>
              {!criticalPathEnabled ? t('toolbar.enableCritical') : t('toolbar.critical')}
              {criticalPathEnabled && criticalItemCount > 0 && (
                <span className={`text-[10px] px-1 py-0.5 rounded ${showCriticalPathOnly ? 'bg-[#EF4444]/10' : 'bg-gray-100'}`}>
                  {criticalItemCount}
                </span>
              )}
            </button>
          )}

          {/* Fullscreen — prominent */}
          <button onClick={onToggleFullscreen}
            className={`flex items-center gap-1.5 border-2 px-3.5 py-2 rounded-lg text-xs font-bold cursor-pointer whitespace-nowrap transition-colors shadow-sm ${isFullscreen ? 'bg-[#E8621A] text-white border-[#E8621A] hover:bg-[#B84C10]' : 'bg-[#FFF0E8] border-[#E8621A] text-[#E8621A] hover:bg-[#FFE4D4]'}`}>
            <i className={`text-base ${isFullscreen ? 'ri-fullscreen-exit-line' : 'ri-fullscreen-line'}`}></i>
            {isFullscreen ? t('toolbar.exit') : t('toolbar.fullscreen')}
          </button>

          {/* More Actions dropdown */}
          {canEdit && (
            <div className="relative" ref={advRef}>
              <button onClick={() => setShowAdvanced((v) => !v)}
                className="flex items-center gap-1.5 bg-white border border-gray-200 text-[#6B7280] px-3 py-1.5 rounded-lg text-xs font-semibold cursor-pointer whitespace-nowrap hover:bg-gray-50 transition-colors">
                <i className="ri-more-line"></i>{t('toolbar.more')}
              </button>
              {showAdvanced && (
                <div className="absolute right-0 top-full mt-1 bg-white border border-gray-200 rounded-xl shadow-lg z-50 w-60 py-1">
                  {/* Schedule Online (future) */}
                  <div className="px-3 py-2 border-b border-gray-100">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <i className="ri-global-line text-[#9CA3AF]"></i>
                        <span className="text-xs text-[#374151] font-medium">{t('toolbar.scheduleOnline')}</span>
                      </div>
                      <span className="text-[9px] text-[#9CA3AF] bg-gray-100 px-1.5 py-0.5 rounded">{t('toolbar.comingSoon')}</span>
                    </div>
                  </div>

                  {/* Export */}
                  {onOpenExportPanel && (
                    <button onClick={() => { setShowAdvanced(false); onOpenExportPanel(); }}
                      className="w-full text-left px-3 py-2 text-xs text-[#374151] hover:bg-gray-50 cursor-pointer flex items-center gap-2">
                      <i className="ri-download-line text-[#9CA3AF]"></i>{t('toolbar.exportPdfCsv')}
                    </button>
                  )}
                  {/* History */}
                  {onOpenHistoryPanel && (
                    <button onClick={() => { setShowAdvanced(false); onOpenHistoryPanel(); }}
                      className="w-full text-left px-3 py-2 text-xs text-[#374151] hover:bg-gray-50 cursor-pointer flex items-center gap-2">
                      <i className="ri-history-line text-[#9CA3AF]"></i>{t('toolbar.scheduleHistory')}
                    </button>
                  )}
                  <div className="border-t border-gray-100 my-1"></div>
                  {/* Add Item */}
                  {onAddItem && (
                    <button onClick={() => { setShowAdvanced(false); onAddItem(); }}
                      className="w-full text-left px-3 py-2 text-xs text-[#374151] hover:bg-gray-50 cursor-pointer flex items-center gap-2">
                      <i className="ri-add-line text-[#E8621A]"></i>{t('toolbar.addScheduleItem')}
                    </button>
                  )}
                  {/* Show Links */}
                  {viewMode === 'gantt' && onToggleShowAllLinks && (
                    <button onClick={() => { setShowAdvanced(false); onToggleShowAllLinks(); }}
                      className="w-full text-left px-3 py-2 text-xs text-[#374151] hover:bg-gray-50 cursor-pointer flex items-center gap-2">
                      <i className={`${showAllLinks ? 'ri-eye-off-line' : 'ri-eye-line'} text-[#9CA3AF]`}></i>
                      {showAllLinks ? t('toolbar.hideAllLinks') : t('toolbar.showAllLinks')}
                    </button>
                  )}
                  <div className="border-t border-gray-100 my-1"></div>
                  {/* Excluded Items */}
                  <div className="px-3 py-2">
                    <p className="text-[10px] font-bold text-[#9CA3AF] uppercase tracking-wider mb-1.5">{t('toolbar.visibility')}</p>
                    <button onClick={() => { setShowAdvanced(false); onToggleShowExcluded(); }}
                      className="w-full text-left text-xs text-[#374151] hover:bg-gray-50 cursor-pointer px-0 py-1 flex items-center gap-2">
                      <i className={`w-4 h-4 flex items-center justify-center ${showExcluded ? 'ri-eye-off-line' : 'ri-eye-line'} text-[#9CA3AF]`}></i>
                      {showExcluded ? t('toolbar.hideExcluded') : t('toolbar.showExcluded')}
                    </button>
                  </div>
                  <button onClick={() => { setShowAdvanced(false); onIncludeAll(); }} disabled={saving || itemCount === 0}
                    className="w-full text-left px-3 py-2 text-xs text-[#374151] hover:bg-gray-50 cursor-pointer disabled:opacity-40">
                    <i className="ri-checkbox-multiple-line mr-1.5 text-[#9CA3AF]"></i>{t('toolbar.includeAll', { n: itemCount })}
                  </button>
                  <button onClick={() => { setShowAdvanced(false); onExcludeAll(); }} disabled={saving || itemCount === 0}
                    className="w-full text-left px-3 py-2 text-xs text-[#374151] hover:bg-gray-50 cursor-pointer disabled:opacity-40">
                    <i className="ri-checkbox-blank-line mr-1.5 text-[#9CA3AF]"></i>{t('toolbar.excludeAll')}
                  </button>
                  <div className="border-t border-gray-100 my-1"></div>
                  <p className="px-3 pt-1 text-[10px] font-bold text-[#9CA3AF] uppercase tracking-wider">{t('toolbar.actions')}</p>
                  <button onClick={() => { setShowAdvanced(false); onGenerateTimeline(); }}
                    className="w-full text-left px-3 py-2 text-xs text-[#374151] hover:bg-gray-50 cursor-pointer flex items-center gap-2">
                    <i className="ri-calendar-event-line mr-1.5 text-[#E8621A]"></i>
                    {itemCount > 0 ? t('toolbar.regenerateTimeline') : t('toolbar.createTimeline')}
                  </button>
                  {onImportBuildertrend && (
                    <button onClick={() => { setShowAdvanced(false); onImportBuildertrend(); }}
                      className="w-full text-left px-3 py-2 text-xs text-[#374151] hover:bg-gray-50 cursor-pointer flex items-center gap-2">
                      <i className="ri-file-upload-line mr-1.5 text-[#E8621A]"></i>{t('toolbar.importBuildertrend')}
                    </button>
                  )}
                  {onCopyGanttColorsFromMethuen && (
                    <button onClick={() => { setShowAdvanced(false); onCopyGanttColorsFromMethuen(); }}
                      className="w-full text-left px-3 py-2 text-xs text-[#374151] hover:bg-gray-50 cursor-pointer flex items-center gap-2">
                      <i className="ri-palette-line mr-1.5 text-[#EC2590]"></i>{t('toolbar.copyGanttColors')}
                    </button>
                  )}
                  {onRecalculateProgress && (
                    <button onClick={() => { setShowAdvanced(false); onRecalculateProgress(); }}
                      className="w-full text-left px-3 py-2 text-xs text-[#374151] hover:bg-gray-50 cursor-pointer flex items-center gap-2">
                      <i className="ri-calculator-line mr-1.5 text-[#059669]"></i>{t('toolbar.recalcProgress')}
                    </button>
                  )}
                  {onRecalcCriticalPath && (
                    <button onClick={() => { setShowAdvanced(false); onRecalcCriticalPath(); }}
                      className="w-full text-left px-3 py-2 text-xs text-[#374151] hover:bg-gray-50 cursor-pointer flex items-center gap-2">
                      <i className="ri-route-line mr-1.5 text-[#EF4444]"></i>{t('toolbar.recalcCriticalPath')}
                    </button>
                  )}
                  {onRecalcLinks && (
                    <button onClick={() => { setShowAdvanced(false); onRecalcLinks(); }}
                      className="w-full text-left px-3 py-2 text-xs text-[#374151] hover:bg-gray-50 cursor-pointer flex items-center gap-2">
                      <i className="ri-links-line mr-1.5 text-[#9CA3AF]"></i>{t('toolbar.recalcLinkedItems')}
                    </button>
                  )}
                  {onSetBaseline && (
                    <button onClick={() => { setShowAdvanced(false); onSetBaseline(); }}
                      className="w-full text-left px-3 py-2 text-xs text-[#374151] hover:bg-gray-50 cursor-pointer flex items-center gap-2">
                      <i className="ri-flag-line mr-1.5 text-[#9CA3AF]"></i>{t('toolbar.setBaseline')}
                    </button>
                  )}
                  {onResetCriticalPathFlags && (
                    <button onClick={() => { setShowAdvanced(false); onResetCriticalPathFlags(); }}
                      className="w-full text-left px-3 py-2 text-xs text-[#EF4444] hover:bg-gray-50 cursor-pointer flex items-center gap-2">
                      <i className="ri-reset-left-line mr-1.5 text-[#EF4444]"></i>{t('toolbar.resetCriticalPath')}
                    </button>
                  )}
                  {onToggleDebug && (
                    <button onClick={() => { setShowAdvanced(false); onToggleDebug(); }}
                      className="w-full text-left px-3 py-2 text-xs text-[#374151] hover:bg-gray-50 cursor-pointer flex items-center gap-2">
                      <i className="ri-bug-line mr-1.5 text-[#9CA3AF]"></i>{t('toolbar.debugInfo')}
                    </button>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Summary toggle */}
          {onToggleSummary && (
            <button onClick={onToggleSummary}
              className={`flex items-center gap-1.5 border px-3 py-1.5 rounded-lg text-xs font-semibold cursor-pointer whitespace-nowrap transition-colors ${showSummary ? 'bg-white border-gray-200 text-[#374151]' : 'bg-[#F9FAFB] border-gray-200 text-[#9CA3AF]'}`}
              title={showSummary ? t('toolbar.hideSummary') : t('toolbar.showSummary')}>
              <i className={showSummary ? 'ri-layout-top-line' : 'ri-layout-top-2-line'}></i>
              {showSummary ? t('toolbar.hideSummary') : t('toolbar.showSummary')}
            </button>
          )}
        </div>
      </div>

        {/* Stage filter row */}
        <div className="flex items-center gap-2 flex-wrap">
          <select value={mainStageFilter}
            onChange={(e) => { onSetMainStageFilter(e.target.value); onSetConstructionSubStageFilter('all'); }}
            className="bg-white border border-gray-200 rounded-lg px-3 py-1.5 text-xs text-[#374151] cursor-pointer hover:border-gray-300 transition-colors">
            <option value="all">{t('toolbar.allStages')}</option>
            {STAGE_OPTIONS.map((s) => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>
          {mainStageFilter === 'Construction' && (
            <select value={constructionSubStageFilter}
              onChange={(e) => onSetConstructionSubStageFilter(e.target.value)}
              className="bg-white border border-gray-200 rounded-lg px-3 py-1.5 text-xs text-[#374151] cursor-pointer hover:border-gray-300 transition-colors">
              <option value="all">{t('checklist.allConstructionSubStages')}</option>
              {CONSTRUCTION_SUB_STAGE_OPTIONS.map((s) => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
          )}
          {(mainStageFilter !== 'all' || constructionSubStageFilter !== 'all') && (
            <button onClick={() => { onSetMainStageFilter('all'); onSetConstructionSubStageFilter('all'); }}
              className="flex items-center gap-1 text-[#9CA3AF] text-xs cursor-pointer hover:text-[#E8621A] transition-colors whitespace-nowrap">
              <i className="ri-close-circle-line"></i>{t('toolbar.clearStageFilters')}
            </button>
          )}
        </div>

        {/* NEW: Pending status filter row (desktop) */}
        {onSetPendingFilter && (
          <div className="flex items-center gap-1.5 flex-wrap">
            <span className="text-[10px] font-bold text-[#9CA3AF] uppercase tracking-wider mr-1">{t('toolbar.statusLabel')}</span>
            {[
              { key: 'all', label: t('toolbar.statusAll') },
              { key: 'pending_final', label: t('toolbar.statusPending') },
              { key: 'final_100', label: t('toolbar.statusDone') },
              { key: 'no_mapping', label: t('toolbar.statusNoCL') },
              { key: 'incomplete_checklist', label: t('toolbar.statusIncCL') },
            ].map((opt) => (
              <button
                key={opt.key}
                onClick={() => onSetPendingFilter(opt.key as typeof pendingFilter)}
                className={`px-2.5 py-1 rounded-full text-[11px] font-semibold cursor-pointer whitespace-nowrap transition-colors ${
                  pendingFilter === opt.key
                    ? 'bg-[#E8621A] text-white'
                    : 'bg-white border border-gray-200 text-[#374151] hover:bg-gray-50'
                }`}
              >
                {opt.label}
              </button>
            ))}
            {pendingFilter !== 'all' && (
              <button
                onClick={() => onSetPendingFilter('all')}
                className="text-[#9CA3AF] text-[11px] cursor-pointer hover:text-[#E8621A] transition-colors whitespace-nowrap"
              >
                <i className="ri-close-circle-line mr-0.5"></i>{t('toolbar.clearFilter')}
              </button>
            )}
          </div>
        )}

        {/* NEW: Pending items quick-action bar (always visible when there are pending items) */}
        {canBulkSelect && pendingFilter !== 'final_100' && (
          <div className="flex items-center gap-3 bg-[#FEF0E7] border border-[#FDBA74] rounded-xl px-3 py-2">
            <i className="ri-time-line text-[#F59E0B] text-sm"></i>
            <span className="text-[#92400E] text-xs font-semibold">
              {t('toolbar.pendingItems', { n: pendingCount, plural: pendingCount !== 1 ? 's' : '' })}
            </span>
            <div className="flex items-center gap-2 ml-auto">
              {onMarkAllPendingComplete && (
                <button
                  onClick={onMarkAllPendingComplete}
                  disabled={bulkMarking}
                  className="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-[#059669] text-white text-xs font-bold cursor-pointer whitespace-nowrap disabled:opacity-50 hover:bg-[#047857] transition-colors"
                >
                  {bulkMarking ? (
                    <><i className="ri-loader-4-line animate-spin"></i>{t('toolbar.marking')}</>
                  ) : (
                    <><i className="ri-checkbox-multiple-line"></i>{t('toolbar.markAllPendingComplete')}</>
                  )}
                </button>
              )}
            </div>
          </div>
        )}

        {/* NEW: Bulk selection bar — desktop */}
        {canBulkSelect && selectedItemIds && selectedItemIds.size > 0 && (
          <div className="flex items-center gap-3 bg-[#FEF0E7] border border-[#FDBA74] rounded-xl px-3 py-2">
            <span className="text-[#92400E] text-xs font-semibold">
              {selectedItemIds.size} {t('toolbar.selected')}
            </span>
            <div className="flex items-center gap-2 ml-auto">
              <button
                onClick={onSelectAllVisible}
                className="text-[#92400E] text-[11px] font-semibold cursor-pointer whitespace-nowrap hover:underline"
              >
                {t('toolbar.selectAll')}
              </button>
              <button
                onClick={onClearSelection}
                className="text-[#9CA3AF] text-[11px] font-semibold cursor-pointer whitespace-nowrap hover:text-[#374151]"
              >
                {t('toolbar.clear')}
              </button>
              <button
                onClick={onMarkAllPendingComplete}
                disabled={bulkMarking}
                className="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-[#059669] text-white text-xs font-bold cursor-pointer whitespace-nowrap disabled:opacity-50 hover:bg-[#047857] transition-colors"
              >
                {bulkMarking ? (
                  <><i className="ri-loader-4-line animate-spin"></i>...</>
                ) : (
                  <><i className="ri-checkbox-multiple-line"></i>{t('toolbar.markAllPending')}</>
                )}
              </button>
              <button
                onClick={onBulkMarkComplete}
                disabled={bulkMarking}
                className="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-[#E8621A] text-white text-xs font-bold cursor-pointer whitespace-nowrap disabled:opacity-50 hover:bg-[#B84C10] transition-colors"
              >
                {bulkMarking ? (
                  <><i className="ri-loader-4-line animate-spin"></i>{t('toolbar.marking')}</>
                ) : (
                  <><i className="ri-checkbox-circle-line"></i>{t('toolbar.markSelected')}</>
                )}
              </button>
            </div>
          </div>
        )}
      </div>
  );
}